// useKira.js — global state + actions for the K.I.R.A floating assistant.
// Reuses POST /api/ai/chat (no direct LLM calls).
import { useState, useCallback, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../context/AuthContext";
import { mapEmotion } from "../components/kira/KiraAvatar";
import { actionOutcome, OUTCOME_PENDING } from "./actionOutcome";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const WELCOMED_KEY = "kira_welcomed";

// Chat history is now persisted server-side via /api/kira/threads.  The
// previous localStorage layer (kira_history_<uid>) has been removed in
// favour of thread-scoped DB storage.

let _msgId = 1;
const nextId = () => `kira-${Date.now()}-${_msgId++}`;

// Safety net for em/en dashes in case a model slips past the system prompt
// + backend post-processing. Mirrors the Python _strip_dashes helper.
const cleanReply = (text) => {
  if (typeof text !== "string") return text;
  return text
    .replace(/—/g, ",")
    .replace(/–/g, ",")
    .replace(/,\s*,/g, ",")
    .replace(/,\s*\./g, ".")
    .trim();
};

export default function useKira() {
  const auth = useContext(AuthContext);
  const user = auth?.user || null;

  const [messages,       setMessages]       = useState([]);
  const [threadId,       setThreadId]       = useState(null);
  const [emotion,        setEmotion]        = useState("neutral");
  const [isOpen,         setIsOpen]         = useState(false);
  const [isLoading,      setIsLoading]      = useState(false);
  const [isTyping,       setIsTyping]       = useState(false);
  const [hasInsight,     setHasInsight]     = useState(false);
  const [unreadCount,    setUnreadCount]    = useState(0);
  const [pendingAction,  setPendingAction]  = useState(null);
  // Default false — only flipped true once we know there's a real authed
  // user.  Lazy init from sessionStorage caused the avatar to flash on the
  // login page (no user yet, but sessionStorage said "not welcomed").
  const [showWelcome,    setShowWelcome]    = useState(false);
  // ── Conversational action state (new flow — replaces pendingAction modals) ──
  const [actionCard,    setActionCard]    = useState(null);
  const [actionList,    setActionList]    = useState(null);
  const [isExecuting,   setIsExecuting]   = useState(false);
  const [modalPosition,  setModalPosition]  = useState(() => ({
    left: Math.max(16, (typeof window !== "undefined" ? window.innerWidth  : 1024) - 520 - 24),
    top:  Math.max(16, (typeof window !== "undefined" ? window.innerHeight : 768)  - 620 - 100),
  }));

  const greetedRef     = useRef(false);
  const typingTimerRef = useRef(null);
  const celebrateRef   = useRef(null);

  // ── Thread API helpers (server-side conversation persistence) ──

  const createThread = useCallback(async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_BASE}/api/kira/threads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setThreadId(data.id);
      return data.id;
    } catch (e) {
      console.error("Failed to create thread", e);
      return null;
    }
  }, []);

  const loadThreadMessages = useCallback(async (tid) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_BASE}/api/kira/threads/${tid}/messages`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await res.json();
      const msgs = (Array.isArray(data) ? data : []).map(m => ({
        id:                       m.id,
        role:                     m.role,
        content:                  m.content,
        action_card:              m.action_card,
        action_card_executed:     m.action_card_executed,
        action_card_executed_at:  m.action_card_executed_at,
        tag:                      m.tag,
        timestamp:                m.created_at,
      }));
      setMessages(msgs);
    } catch (e) {
      console.error("Failed to load messages", e);
    }
  }, []);

  const saveMessage = useCallback(async (tid, message) => {
    if (!tid) return null;
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_BASE}/api/kira/threads/${tid}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          role:                    message.role,
          content:                 message.content,
          action_card:             message.action_card             || null,
          action_card_executed:    message.action_card_executed    || false,
          action_card_executed_at: message.action_card_executed_at || null,
          tag:                     message.tag                     || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        return data.id || null;   // DB UUID for later PATCH calls
      }
    } catch (e) {
      // Non-fatal — message still shows in UI
      console.error("Failed to save message", e);
    }
    return null;
  }, []);

  const updateMessage = useCallback(async (messageId, updates) => {
    if (!threadId || !messageId) return;
    const token = localStorage.getItem("token");
    try {
      await fetch(`${API_BASE}/api/kira/threads/messages/${messageId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
    } catch (e) {
      // Non-fatal
    }
  }, [threadId]);

  // ── Internal: append message (also persists to current thread + reconciles
  //    the temp client id with the DB UUID once the POST returns).
  const pushMessage = useCallback(async (m) => {
    const newMessage = { id: nextId(), timestamp: new Date().toISOString(), ...m };
    setMessages(prev => [...prev, newMessage]);
    const dbId = await saveMessage(threadId, newMessage);
    if (dbId) {
      setMessages(prev => prev.map(msg =>
        msg.id === newMessage.id ? { ...msg, id: dbId } : msg
      ));
    }
  }, [threadId, saveMessage]);

  // ── Public: notice typing (debounced auto-clear after 1.5s)
  const noticeTyping = useCallback(() => {
    setIsTyping(true);
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => setIsTyping(false), 1500);
  }, []);

  // ── Internal: call /api/ai/chat
  const callChat = useCallback(async (message) => {
    const token  = localStorage.getItem("token");
    const uid    = user?.id || user?.user_id || "anon";
    // Separate Redis memory namespace from main /chat — see ai_agent/orchestrator.py
    const sessionId = `kira_float_${uid}`;
    const res = await fetch(`${API_BASE}/api/ai/chat`, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:  `Bearer ${token}`,
      },
      body: JSON.stringify({
        message,
        surface:    "kira_float",
        session_id: sessionId,
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Chat failed (${res.status})`);
    }
    return res.json();
  }, [user]);

  // ── Opening greeting (first time we open)
  const fetchOpeningGreeting = useCallback(async () => {
    if (greetedRef.current) return;
    greetedRef.current = true;

    const firstName = user?.full_name?.split(" ")[0] || "there";
    setIsLoading(true);
    setEmotion("thinking");

    const hour      = new Date().getHours();
    const timeOfDay = hour < 12 ? "morning" :
                      hour < 17 ? "afternoon" : "evening";
    const dayOfWeek = new Date().toLocaleDateString("en-GB", { weekday: "long" });
    const isMonday  = dayOfWeek === "Monday";
    const isFriday  = dayOfWeek === "Friday";

    try {
      // Send a benign user message; the backend orchestrator's system
      // prompt + "Start of new session" cue handle the warm greeting.
      // Sending an instruction-shaped message ("You are greeting ...")
      // tripped Claude's safety filter and caused refusals on first open.
      const data = await callChat("hi");

      const reply = cleanReply(data.reply || data.message || "Hi! I'm here when you need me.");
      pushMessage({ role: "assistant", content: reply, tag: data.tag });
      setEmotion(mapEmotion(data.tag, reply));
      setHasInsight(true);
    } catch (e) {
      pushMessage({
        role: "assistant",
        content: `Hi ${firstName}! I'm K.I.R.A. I'm having trouble reaching the server right now, but I'll be ready when you need me.`,
      });
      setEmotion("neutral");
    } finally {
      setIsLoading(false);
    }
  }, [user, callChat, pushMessage]);

  // No auto-open effect — welcome is now its own route (/welcome) and
  // fetchOpeningGreeting is called from KiraWelcomePage on mount, plus
  // from openKira on first bubble click.  showWelcome / dismissWelcome
  // remain in the API surface for callers that still want to gate UI.

  const dismissWelcome = useCallback(() => {
    try { sessionStorage.setItem(WELCOMED_KEY, "true"); } catch {}
    setShowWelcome(false);
  }, []);

  // ── Public: open / close (modal opens at saved corner position)
  const openKira = useCallback(() => {
    setUnreadCount(0);
    setHasInsight(false);
    if (messages.length === 0) {
      fetchOpeningGreeting();
    }
    if (!threadId) {
      createThread();
    }
    setIsOpen(true);
  }, [messages.length, fetchOpeningGreeting, threadId, createThread]);

  // ── New login → fresh thread.  Fires when user.id changes (post-login
  // or after a session refresh that re-hydrates user from storage).
  useEffect(() => {
    if (user?.id) {
      setMessages([]);
      setThreadId(null);
      createThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const closeKira = useCallback(() => {
    setIsOpen(false);
    // messages persist — don't clear
  }, []);

  // ── Public: send a user message
  const sendMessage = useCallback(async (userText) => {
    const text = (userText || "").trim();
    if (!text || isLoading) return;

    pushMessage({ role: "user", content: text });
    setIsLoading(true);
    setEmotion("thinking");

    try {
      const data  = await callChat(text);
      const reply = cleanReply(data.reply || data.message || "I didn't get a response. Please try again.");

      // ── Conversational action payloads — render alongside the message ──
      let cardForMsg = null;
      let listForMsg = null;
      if (data.action_card) {
        cardForMsg = data.action_card;
        setActionCard(data.action_card);
        setActionList(null);
        setEmotion("alert");
      } else if (data.action_list) {
        listForMsg = data.action_list;
        setActionList(data.action_list);
        setActionCard(null);
        setEmotion("alert");
      } else {
        // Plain reply — clear any stale card/list so old UI doesn't linger.
        if (data.tag === "action_cancelled" || data.tag === "action_empty") {
          setActionCard(null);
          setActionList(null);
        }
      }

      pushMessage({
        role:        "assistant",
        content:     reply,
        tag:         data.tag,
        action_card: cardForMsg,
        action_list: listForMsg,
      });
      if (!cardForMsg && !listForMsg) setEmotion(mapEmotion(data.tag, reply));

      // Legacy form-modal flow (kept for non-conversational actions).
      if (data.type === "action" && data.action) {
        setPendingAction({
          type:        data.action,
          details:     data.details || {},
          description: reply,
        });
        setEmotion("alert");
      }

      if (!isOpen) setUnreadCount(c => c + 1);
    } catch (e) {
      pushMessage({
        role: "assistant",
        content: "Sorry — I'm having trouble connecting right now. Please try again in a moment.",
      });
      setEmotion("error");
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, isOpen, callChat, pushMessage]);

  // ── Public: confirm pending action (called by InlineActionForm onSuccess)
  const confirmAction = useCallback((successMsg) => {
    setPendingAction(null);
    pushMessage({
      role: "assistant",
      content: successMsg || "Done! I've completed that for you.",
    });
    // Celebrating burst (1.5s) then settle to happy
    setEmotion("celebrating");
    clearTimeout(celebrateRef.current);
    celebrateRef.current = setTimeout(() => setEmotion("happy"), 1500);

    // Re-prime insight after context likely changed
    setTimeout(() => setHasInsight(true), 2000);
  }, [pushMessage]);

  // ── Public: cancel pending action (called by InlineActionForm onDismiss)
  const cancelAction = useCallback(() => {
    setPendingAction(null);
    pushMessage({
      role: "assistant",
      content: "No problem, I've cancelled that. Anything else I can help with?",
    });
    setEmotion("neutral");
  }, [pushMessage]);

  // ── Conversational action endpoints ───────────────────────────
  const _kiraSession = useCallback(() => {
    const uid = user?.id || user?.user_id || "anon";
    return `kira_float_${uid}`;
  }, [user]);

  const _post = useCallback(async (path, body) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}${path}`, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:  `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(txt || `Request failed (${res.status})`);
    }
    return res.json();
  }, []);

  const selectActionItem = useCallback(async (item) => {
    if (!item || !actionList) return;
    setIsExecuting(true);
    try {
      const data = await _post("/api/ai/action/select", {
        session_id:  _kiraSession(),
        item_id:     item.id,
        item_params: item.params || {},
      });
      const reply = cleanReply(data.reply || "");
      let nextCard = null;
      let nextList = null;
      if (data.action_card) { nextCard = data.action_card; setActionCard(data.action_card); }
      if (data.action_list) { nextList = data.action_list; setActionList(data.action_list); }
      if (data.action_card) setActionList(null);  // moved past selection
      pushMessage({
        role: "assistant",
        content: reply,
        tag: data.tag,
        action_card: nextCard,
        action_list: nextList,
      });
    } catch (e) {
      pushMessage({
        role: "assistant",
        content: "Couldn't record that selection — try again?",
      });
      setEmotion("error");
    } finally {
      setIsExecuting(false);
    }
  }, [actionList, _kiraSession, _post, pushMessage]);

  const confirmActionCard = useCallback(async () => {
    if (!actionCard) return;
    setIsExecuting(true);
    try {
      const data = await _post("/api/ai/action/confirm", {
        session_id:  _kiraSession(),
        action_type: actionCard.action_type,
        params:      actionCard.params || {},
      });

      // The consent gate at dispatch can hand back a PROPOSAL instead of a
      // result. That means nothing was written, so never report it as done:
      // keep the card live and let the user approve it deliberately.
      if (data.requires_approval) {
        pushMessage({
          role:        "assistant",
          content:     cleanReply(data.reply || "I need you to approve that first."),
          action_card: data.action_card || actionCard,
        });
        if (data.action_card) setActionCard(data.action_card);
        setEmotion("alert");
        return;
      }

      // THREE OUTCOMES, NOT TWO. See hooks/actionOutcome.js: an endpoint
      // may now answer "accepted, pending approval" (202), which is
      // neither done nor failed. PATCH /api/profile/bank does exactly
      // that — it raises a WF04 approval request instead of writing.
      const outcome = actionOutcome(data);
      const reply = cleanReply(outcome.reply);

      // Mark the message that introduced this card as executed (latest
      // unexecuted message whose action_card matches the live one). The card
      // stays in the conversation history in a read-only "Submitted" state —
      // true for a pending submission as well as a completed one.
      if (outcome.stampCard) {
        const stamp = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
        let executedId = null;
        setMessages(prev => {
          for (let i = prev.length - 1; i >= 0; i--) {
            const m = prev[i];
            if (m?.action_card && !m.action_card_executed) {
              executedId = m.id;
              const next = prev.slice();
              next[i] = { ...m, action_card_executed: true, action_card_executed_at: stamp };
              return next;
            }
          }
          return prev;
        });
        if (executedId) {
          updateMessage(executedId, {
            action_card_executed:    true,
            action_card_executed_at: stamp,
          });
        }
      }

      pushMessage({ role: "assistant", content: reply });
      setActionCard(null);
      setActionList(null);
      if (outcome.refreshModules) {
        // Notify any listening module page to refresh — the relevant action
        // types are matched in each page's useEffect handler. Deliberately
        // NOT fired for a pending submission: nothing changed, and a refetch
        // that redisplays the OLD bank details immediately after a
        // "submitted" message reads as the save having silently failed.
        try {
          window.dispatchEvent(new CustomEvent("kira_action_complete", {
            detail: { action_type: actionCard?.action_type },
          }));
        } catch (e) {}
      }
      if (outcome.celebrate) {
        setEmotion("celebrating");
        clearTimeout(celebrateRef.current);
        celebrateRef.current = setTimeout(() => setEmotion("happy"), 1500);
      } else if (outcome.kind === OUTCOME_PENDING) {
        // Acknowledged, not celebrated. The celebration is the strongest
        // "it's done" signal in this UI and this is the one case where it
        // is not done.
        setEmotion("happy");
      } else {
        setEmotion("error");
      }
    } catch (e) {
      pushMessage({
        role: "assistant",
        content: "That didn't go through. Want to try again?",
      });
      setEmotion("error");
    } finally {
      setIsExecuting(false);
    }
  }, [actionCard, _kiraSession, _post, pushMessage, updateMessage]);

  const cancelActionCard = useCallback(async () => {
    setActionCard(null);
    setActionList(null);
    setIsExecuting(false);
    // Strip the action_card from the latest unexecuted message so the
    // cancelled card doesn't linger in history with live-looking buttons.
    setMessages(prev => {
      for (let i = prev.length - 1; i >= 0; i--) {
        const m = prev[i];
        if (m?.action_card && !m.action_card_executed) {
          const next = prev.slice();
          const { action_card, ...rest } = m;
          next[i] = rest;
          return next;
        }
      }
      return prev;
    });
    try {
      await _post("/api/ai/action/cancel", { session_id: _kiraSession() });
    } catch {}
    pushMessage({
      role: "assistant",
      content: "Cancelled. Anything else I can help with?",
    });
    setEmotion("neutral");
  }, [_kiraSession, _post, pushMessage]);

  return {
    // state
    messages, emotion, isOpen, isLoading, isTyping,
    hasInsight, unreadCount, pendingAction,
    showWelcome,
    modalPosition,
    threadId,
    // conversational action state
    actionCard, actionList, isExecuting,
    // setters
    setModalPosition,
    setMessages,
    setThreadId,
    // actions
    openKira, closeKira,
    sendMessage,
    confirmAction, cancelAction,
    noticeTyping,
    dismissWelcome,
    fetchOpeningGreeting,
    // thread persistence
    createThread, loadThreadMessages, updateMessage,
    // conversational action handlers
    selectActionItem, confirmActionCard, cancelActionCard,
  };
}
